import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { useGetUserDetails } from "src/helpers/UserDetails";
import { useGetUserSettings } from "src/views/users/SettingsService";
import {
  ensureDesktopPermission,
  prefsFromSettings,
  runNotificationEffects,
  unlockNotificationAudio,
} from "./effects";
import {
  getNotificationStreamCursor,
  isNotificationStreamHealthy,
  resetNotificationStreamCursor,
  subscribeNotificationStreamHealth,
  useNotificationStream,
} from "src/reusable/hooks/useNotificationStream";
import { markNotificationsSeen } from "./api";
import {
  claimNotification,
  deliveryDelayMs,
  rememberThisTab,
} from "./delivery";
import { isOnScreen, useUserNotificationStore } from "./store";
import { applyTabBadge, applyTitleBadge } from "./tabBadge";
import {
  isStaleActive,
  isUserNotification,
  NOTIFICATION_EVENT,
  resourceKey,
  userEventStreamKey,
} from "./types";
import { tabInBackground } from "./visibility";

/**
 * Subscribe the signed-in user to their personal notification stream and
 * feed it into the store. Mount once (the header bell does). The store is
 * per-user state in a module-level singleton, so it is wiped whenever the
 * user id changes (logout → login is an SPA navigation), and so is the
 * stream's resume point: resuming live after a sign-in would toast what
 * finished while signed out instead of backfilling it as history.
 */
export function useUserNotificationStream(): void {
  const userId = useGetUserDetails()?.id;
  const ingest = useUserNotificationStore((s) => s.ingest);
  const reset = useUserNotificationStore((s) => s.reset);
  const setSubscribedAt = useUserNotificationStore((s) => s.setSubscribedAt);

  useEffect(() => {
    reset();
    resetNotificationStreamCursor();
    if (userId == null) return;
    setSubscribedAt(Date.now());
    return () => setSubscribedAt(null);
  }, [userId, reset, setSubscribedAt]);

  useNotificationStream(
    userId != null ? userEventStreamKey(userId) : null,
    NOTIFICATION_EVENT,
    (data) => {
      if (isUserNotification(data)) {
        ingest(data);
      } else {
        console.warn("Dropping malformed user notification", data);
      }
    },
  );
}

/** Live status of one resource; null when nothing is known (or the last
 *  active status is too old to still be true). */
export function useResourceStatus(
  type: string,
  id: string | number | null | undefined,
): string | null {
  return useUserNotificationStore((s) => {
    if (id == null) return null;
    const current = s.resourceStatus[resourceKey(type, id)];
    if (!current || isStaleActive(current)) return null;
    return current.status;
  });
}

/**
 * Declare that `id` is on screen: its current terminal status is cleared
 * (back to idle), its bell entries are read, and any terminal status that
 * arrives while mounted and focused is cleared on arrival. When the tab
 * comes back to the foreground, whatever arrived meanwhile is seen too.
 */
export function useViewingResource(
  type: string,
  id: string | number | null | undefined,
): void {
  const setViewing = useUserNotificationStore((s) => s.setViewing);
  const markResourceSeen = useUserNotificationStore((s) => s.markResourceSeen);

  useEffect(() => {
    if (id == null) return;
    setViewing(type, id, true);
    markResourceSeen(type, id);
    const onVisible = () => {
      if (!tabInBackground()) markResourceSeen(type, id);
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
      setViewing(type, id, false);
    };
  }, [type, id, setViewing, markResourceSeen]);
}

/** The user's notification delivery settings (defaults until loaded). */
export function useNotificationPrefs() {
  const { data: settings } = useGetUserSettings();
  return prefsFromSettings(settings);
}

/**
 * Toast / sound / desktop delivery for each new listed notification, per
 * the user's notification settings. Mount once, next to the stream.
 *
 * Delivery waits until the settings query has resolved — the defaults
 * (pop-ups on) must not fire for someone who turned them off — and drains
 * the store's pending queue so a batch delivers every alert. Across tabs,
 * the tab that started the work delivers; see delivery.ts for the rest.
 */
export function useNotificationEffects(): void {
  const history = useHistory();
  const { data: settings, isSuccess: settingsReady } = useGetUserSettings();
  const pendingCount = useUserNotificationStore((s) => s.pending.length);
  const prefs = prefsFromSettings(settings);

  // Audio unlock and the permission prompt both need a user gesture, so
  // piggyback on the next click/keypress once either channel is enabled.
  useEffect(() => {
    if (!prefs.sound && !prefs.desktop) return;
    const onGesture = () => {
      if (prefs.sound) unlockNotificationAudio();
      if (prefs.desktop) void ensureDesktopPermission();
    };
    const opts = { once: true, capture: true };
    window.addEventListener("pointerdown", onGesture, opts);
    window.addEventListener("keydown", onGesture, opts);
    return () => {
      window.removeEventListener("pointerdown", onGesture, opts);
      window.removeEventListener("keydown", onGesture, opts);
    };
  }, [prefs.sound, prefs.desktop]);

  // Deferred deliveries wait on a timer. Draining the queue re-runs this
  // effect (pendingCount → 0), so the timers live in a ref and are only
  // cancelled on unmount — not by that re-run.
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());
  useEffect(() => {
    rememberThisTab();
    const pending = timers.current;
    return () => pending.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    if (!settingsReady || pendingCount === 0) return;
    const store = useUserNotificationStore.getState();
    for (const n of store.drainPending()) {
      const onScreen = isOnScreen(store.viewing, n);
      const open = (link: string) => {
        useUserNotificationStore.getState().markRead(n.id);
        history.push(link);
      };
      const deliver = () => {
        void claimNotification(n.id).then((mine) => {
          if (mine) runNotificationEffects(n, prefs, open, { onScreen });
        });
      };
      const delay = deliveryDelayMs(n);
      if (delay === null) continue;
      if (delay > 0) {
        const timer = setTimeout(() => {
          timers.current.delete(timer);
          deliver();
        }, delay);
        timers.current.add(timer);
      } else {
        deliver();
      }
    }
    // prefs is rebuilt each render; only new pending items should deliver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount, settingsReady]);
}

/**
 * Mirror the unread count onto the browser tab (title prefix, favicon dot,
 * app badge). Re-applies the title prefix whenever the app changes the
 * title underneath it. Mount once, next to the bell.
 */
export function useTabBadge(): void {
  const unread = useUserNotificationStore((s) => s.unreadCount);

  useEffect(() => {
    applyTabBadge(unread);
    const titleEl = document.querySelector("title");
    if (!titleEl) return;
    const observer = new MutationObserver(() => applyTitleBadge(unread));
    observer.observe(titleEl, {
      childList: true,
      characterData: true,
      subtree: true,
    });
    return () => observer.disconnect();
  }, [unread]);

  useEffect(() => () => applyTabBadge(0), []);
}

/**
 * Tell the server when the user has caught up: after any read (clicking an
 * entry, Mark all read, opening the chat, or an alert arriving for the chat
 * on screen) that leaves nothing unread, the read-through cursor moves to
 * the last event this tab received. A wipe (logout, Clear all) is not a
 * read. Mount once, next to the bell.
 */
export function useSeenCursorSync(): void {
  const readVersion = useUserNotificationStore((s) => s.readVersion);
  const unreadCount = useUserNotificationStore((s) => s.unreadCount);
  const listed = useUserNotificationStore((s) => s.notifications.length);
  const acknowledged = useRef(readVersion);

  useEffect(() => {
    if (readVersion === acknowledged.current) return;
    acknowledged.current = readVersion;
    if (unreadCount === 0 && listed > 0) {
      void markNotificationsSeen(getNotificationStreamCursor());
    }
  }, [readVersion, unreadCount, listed]);
}

/** Whether the shared SSE connection that carries notifications is up. */
export function useNotificationStreamHealth(): boolean {
  const [healthy, setHealthy] = useState(() => isNotificationStreamHealthy());
  useEffect(() => subscribeNotificationStreamHealth(setHealthy), []);
  return healthy;
}
